import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySelector from './CurrencySelector';

const Footer = () => {
  const { formatPrice } = useCurrency();
  
  // Scroll to top when clicking any navigation link
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <footer className="bg-[#0E163B] text-white">
      {/* Sticky CTA Bar */}
      <div className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] py-4 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between">
          <div className="text-center sm:text-left mb-3 sm:mb-0">
            <h3 className="text-lg font-bold text-white mb-1">Ready to Transform Your Business?</h3>
            <p className="text-white/90 text-sm">🚀 Start 14-Day Trial for {formatPrice(5)} — Cancel Anytime</p>
          </div>
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <Link to="/pricing" onClick={handleNavClick}>
              Start Your Trial Now
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 lg:col-span-2">
            <Link to="/" onClick={handleNavClick} className="inline-block mb-4">
              <div className="bg-white rounded-lg px-3 py-2 inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_local-hero-site/artifacts/jgz9n22r_Local%20Business%20Heroes%20Logo%20v2.png" 
                  alt="Local Business Heroes" 
                  className="h-10 w-auto"
                />
              </div>
            </Link>
            <p className="text-gray-300 mb-6 max-w-md">
              Helping local businesses become local legends with our all-in-one growth system. 
              More leads, automated follow-up, and the time freedom you deserve.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-[#FF5A3D] transition-colors">
                <Facebook size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#FF5A3D] transition-colors">
                <Twitter size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#FF5A3D] transition-colors">
                <Linkedin size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#FF5A3D] transition-colors">
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/features" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">Features</Link></li>
              <li><Link to="/pricing" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">Pricing</Link></li>
              <li><Link to="/industries" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">Industries</Link></li>
              <li><Link to="/about" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">About Us</Link></li>
              <li><Link to="/blog" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">Blog</Link></li>
              <li><Link to="/contact" onClick={handleNavClick} className="text-gray-300 hover:text-[#FF5A3D] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact Info & Currency */}
          <div>
            <h3 className="font-semibold mb-4">Get In Touch</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 text-gray-300">
                <Phone size={16} />
                <div className="flex flex-col text-sm">
                  <span>UK: +44 20 4634 9070</span>
                  <span>USA: +1 866-540-6506 (Toll Free)</span>
                </div>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <MapPin size={16} />
                <span className="text-sm">United Kingdom</span>
              </div>
            </div>
            <CurrencySelector variant="footer" />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-gray-400 text-sm mb-4 sm:mb-0">
            © 2026 Local Business Heroes. All rights reserved.
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy" onClick={handleNavClick} className="text-gray-400 hover:text-[#FF5A3D] transition-colors">Privacy Policy</Link>
            <Link to="/terms" onClick={handleNavClick} className="text-gray-400 hover:text-[#FF5A3D] transition-colors">Terms of Service</Link>
            <Link to="/dpa" onClick={handleNavClick} className="text-gray-400 hover:text-[#FF5A3D] transition-colors">DPA</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;